import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import React, { useEffect, useState } from "react";
import { alpha } from "@mui/system";
import { getCategoryById, getImportItemById, getImportsByCategory } from "../../../services/Import";
import InputField from "../../../FormComponents/InputField";
import BillWritableTable from "../../../components/BillWritableTable";
import FormAlert from "../../../components/FormAlert";
import ItemSelectingPopup from "../../../components/ItemSelectingPopup";
import Im_CategorySelectingPopup from "../../../components/Im_CategorySelectingPopup";
import CategoryItemsViewDialog from "../../../components/CategoryItemsViewDialog";
import SubOrderProductValidation from "../../../Validation/SubOrderProductValidation";
import {
  getSubOrderProductById,
  updateSubOrderProduct,
} from "../../../services/SubOrderProduct";

function SubOrderProductEdit({ productId, setOpenForm, onSaved }) {
  const [values, setValues] = useState({ _productName: "", _note: "" });

  // import item selection state
  const [selectedInputItems, setSelectedInputItems] = useState([]);
  const [selectedInputRows, setSelectedInputRows] = useState([]);
  const [selectedOutputItems, setSelectedOutputItems] = useState([]);
  const [selectedOutputRows, setSelectedOutputRows] = useState([]);
  const [rows, setRows] = useState([]);
  const [openInputTable, setOpenInputTable] = useState(false);
  const [openOutputTable, setOpenOutputTable] = useState(false);

  // import category selection state — input
  const [openInputCategoryTable, setOpenInputCategoryTable] = useState(false);
  const [selectedInputCategories, setSelectedInputCategories] = useState([]);
  const [inputCategoryRows, setInputCategoryRows] = useState([]);

  // import category selection state — output
  const [openOutputCategoryTable, setOpenOutputCategoryTable] = useState(false);
  const [selectedOutputCategories, setSelectedOutputCategories] = useState([]);
  const [outputCategoryRows, setOutputCategoryRows] = useState([]);

  // category items view dialog
  const [categoryViewOpen, setCategoryViewOpen] = useState(false);
  const [categoryViewName, setCategoryViewName] = useState("");
  const [categoryViewItems, setCategoryViewItems] = useState([]);

  const [error, setError] = useState({});
  const [displayAlert, setAlert] = useState(false);
  const [alertData, setAlertData] = useState({ type: "", message: "" });
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const writeable_page = 0;
  const writeable_rowsPerPage = 100;

  useEffect(() => {
    loadProduct();
  }, [productId]);

  const loadProduct = async () => {
    setIsLoading(true);
    const product = await getSubOrderProductById(productId);
    if (!product || product === "Something went wrong...") {
      setAlertData({ type: "error", message: "Failed to load product data." });
      setAlert(true);
      setIsLoading(false);
      return;
    }

    setValues({ _productName: product.subProductName, _note: "" });

    const inputItems = product.subOrderItems?.filter((i) => i.itemType === "INPUT") || [];
    const outputItems = product.subOrderItems?.filter((i) => i.itemType === "OUTPUT") || [];

    setSelectedInputItems(
      inputItems.filter((i) => i.imports != null).map((i) => i.imports.importId)
    );
    setSelectedOutputItems(
      outputItems.filter((i) => i.imports != null).map((i) => i.imports.importId)
    );
    setSelectedInputCategories(
      inputItems.filter((i) => i.importCategory != null).map((i) => i.importCategory.cat_id)
    );
    setSelectedOutputCategories(
      outputItems.filter((i) => i.importCategory != null).map((i) => i.importCategory.cat_id)
    );

    setSelectedInputRows(
      inputItems.map((i) => {
        if (i.importCategory != null) {
          return {
            item: i.importCategory.category,
            brand: "",
            qty: i.itemQty,
            orderUnit: i.itemUnit,
            unitType: i.itemUnit,
            itemUintsPerOrderUnit: i.prodUnitsForItemUnit,
            importId: null,
            existingQty: null,
            importCategoryId: i.importCategory.cat_id,
            itemId: i.itemId,
          };
        }
        return {
          item: i.imports.itemName + "(" + i.imports.product_code + ")",
          brand: i.imports.brand,
          qty: i.itemQty,
          orderUnit: i.itemUnit,
          unitType: i.imports.unitType,
          itemUintsPerOrderUnit: i.prodUnitsForItemUnit,
          importId: i.imports.importId,
          existingQty: i.imports.qty,
          importCategoryId: null,
          itemId: i.itemId,
        };
      })
    );

    setSelectedOutputRows(
      outputItems.map((i) => {
        if (i.importCategory != null) {
          return {
            item: i.importCategory.category,
            brand: "",
            qty: i.itemQty,
            orderUnit: i.itemUnit,
            unitType: i.itemUnit,
            itemUintsPerOrderUnit: i.prodUnitsForItemUnit,
            importId: null,
            existingQty: null,
            importCategoryId: i.importCategory.cat_id,
            itemId: i.itemId,
          };
        }
        return {
          item: i.imports.itemName + "(" + i.imports.product_code + ")",
          brand: i.imports.brand,
          qty: i.itemQty,
          orderUnit: i.itemUnit,
          unitType: i.imports.unitType,
          itemUintsPerOrderUnit: i.prodUnitsForItemUnit,
          importId: i.imports.importId,
          existingQty: i.imports.qty,
          importCategoryId: null,
          itemId: i.itemId,
        };
      })
    );

    setIsLoading(false);
  };

  const handleChange = (name, val) => {
    setValues({ ...values, [name]: val });
    if (error[name]) setError({ ...error, [name]: "" });
  };

  const itemColumns = [
    { id: "item", label: "Item", minWidth: 100, editable: false },
    { id: "brand", label: "Brand", minWidth: 100, editable: false },
    { id: "qty", label: "Qty", minWidth: 100, editable: true, type: "number", isDecimal: false },
    { id: "orderUnit", label: "Order Unit", minWidth: 100, editable: true },
    { id: "unitType", label: "Unit", minWidth: 100, editable: false },
    { id: "itemUintsPerOrderUnit", label: "Item Units Amount", minWidth: 100, editable: true, type: "number", isDecimal: true },
  ];

  // ── item close handlers ──────────────────────────────────────────────────
  const handleCloseInputTable = async () => {
    setOpenInputTable(false);
    const existingById = Object.fromEntries(
      selectedInputRows.filter((r) => r.importId != null).map((r) => [r.importId, r])
    );
    const result = selectedInputRows.filter((r) => r.importCategoryId != null);
    for (const importId of selectedInputItems) {
      if (existingById[importId]) {
        result.push(existingById[importId]);
      } else {
        const importItem = await getImportItemById(importId);
        result.push({
          item: importItem.itemName + "(" + importItem.product_code + ")",
          brand: importItem.brand, qty: 0.0, orderUnit: importItem.unitType,
          unitType: importItem.unitType, itemUintsPerOrderUnit: 1.0,
          importId: importItem.importId, existingQty: importItem.qty, importCategoryId: null,
        });
      }
    }
    setSelectedInputRows(result);
    if (error._inputItems) setError({ ...error, _inputItems: "" });
  };

  const handleCloseOutputTable = async () => {
    setOpenOutputTable(false);
    const existingById = Object.fromEntries(
      selectedOutputRows.filter((r) => r.importId != null).map((r) => [r.importId, r])
    );
    const result = selectedOutputRows.filter((r) => r.importCategoryId != null);
    for (const importId of selectedOutputItems) {
      if (existingById[importId]) {
        result.push(existingById[importId]);
      } else {
        const importItem = await getImportItemById(importId);
        result.push({
          item: importItem.itemName + "(" + importItem.product_code + ")",
          brand: importItem.brand, qty: 0.0, orderUnit: importItem.unitType,
          unitType: importItem.unitType, itemUintsPerOrderUnit: 1.0,
          importId: importItem.importId, existingQty: importItem.qty, importCategoryId: null,
        });
      }
    }
    setSelectedOutputRows(result);
    if (error._outputItems) setError({ ...error, _outputItems: "" });
  };

  // ── category close handlers ──────────────────────────────────────────────
  const handleCloseInputCategoryTable = async () => {
    setOpenInputCategoryTable(false);
    const existingById = Object.fromEntries(
      selectedInputRows.filter((r) => r.importCategoryId != null).map((r) => [r.importCategoryId, r])
    );
    const result = selectedInputRows.filter((r) => r.importId != null);
    for (const catId of selectedInputCategories) {
      if (existingById[catId]) {
        result.push(existingById[catId]);
      } else {
        const category = await getCategoryById(catId);
        result.push({
          item: category.category, brand: "", qty: 0.0, orderUnit: "", unitType: "",
          itemUintsPerOrderUnit: 1.0, importId: null, existingQty: null,
          importCategoryId: category.cat_id,
        });
      }
    }
    setSelectedInputRows(result);
    if (error._inputItems) setError({ ...error, _inputItems: "" });
  };

  const handleCloseOutputCategoryTable = async () => {
    setOpenOutputCategoryTable(false);
    const existingById = Object.fromEntries(
      selectedOutputRows.filter((r) => r.importCategoryId != null).map((r) => [r.importCategoryId, r])
    );
    const result = selectedOutputRows.filter((r) => r.importId != null);
    for (const catId of selectedOutputCategories) {
      if (existingById[catId]) {
        result.push(existingById[catId]);
      } else {
        const category = await getCategoryById(catId);
        result.push({
          item: category.category, brand: "", qty: 0.0, orderUnit: "", unitType: "",
          itemUintsPerOrderUnit: 1.0, importId: null, existingQty: null,
          importCategoryId: category.cat_id,
        });
      }
    }
    setSelectedOutputRows(result);
    if (error._outputItems) setError({ ...error, _outputItems: "" });
  };

  // ── remove handlers ──────────────────────────────────────────────────────
  const removeInputCategoryRow = (catId) => {
    setSelectedInputRows((prev) => prev.filter((r) => r.importCategoryId !== catId));
    setSelectedInputCategories((prev) => prev.filter((id) => id !== catId));
  };

  const removeOutputCategoryRow = (catId) => {
    setSelectedOutputRows((prev) => prev.filter((r) => r.importCategoryId !== catId));
    setSelectedOutputCategories((prev) => prev.filter((id) => id !== catId));
  };

  // ── view items handler ───────────────────────────────────────────────────
  const handleViewCategoryItems = async (catId, catName) => {
    const items = await getImportsByCategory(catId);
    setCategoryViewName(catName);
    setCategoryViewItems(Array.isArray(items) ? items : []);
    setCategoryViewOpen(true);
  };

  // ── submit ───────────────────────────────────────────────────────────────
  const handleSubmitClick = () => {
    const validationErrors = SubOrderProductValidation(selectedInputRows, selectedOutputRows);
    setError(validationErrors);
    if (Object.keys(validationErrors).length === 0) {
      setOpenConfirmDialog(true);
    } else {
      setAlertData({ type: "error", message: "Please fix the validation errors before submitting" });
      setAlert(true);
    }
  };

  const handleConfirmEdit = async () => {
    setOpenConfirmDialog(false);

    const buildItem = (r, itemType) => {
      const item = {
        itemId: r.itemId || null,
        itemQty: parseFloat(r.qty) || 0,
        itemType,
        itemUnit: r.orderUnit,
        prodUnitsForItemUnit: parseFloat(r.itemUintsPerOrderUnit) || 1,
      };
      if (r.importCategoryId != null) {
        item.importCategoryId = r.importCategoryId;
      } else {
        item.importId = r.importId;
      }
      return item;
    };

    const subOrderItems = [
      ...selectedInputRows.map((r) => buildItem(r, "INPUT")),
      ...selectedOutputRows.map((r) => buildItem(r, "OUTPUT")),
    ];

    const body = { productId, productName: values._productName, subOrderItemBody: subOrderItems };
    const result = await updateSubOrderProduct(productId, body);
    if (result) {
      setAlertData({ type: "success", message: "Sub Order Product updated successfully." });
      setAlert(true);
      setTimeout(() => onSaved(), 1000);
    } else {
      setAlertData({ type: "error", message: "Failed to update sub order product." });
      setAlert(true);
    }
  };

  // ── category table renderer ──────────────────────────────────────────────
  const renderCategoryTable = (categoryRows, onRemove) => (
    <Table size="small" sx={{ mt: 1 }}>
      <TableHead>
        <TableRow>
          <TableCell><strong>Category</strong></TableCell>
          <TableCell align="right"><strong>Actions</strong></TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {categoryRows.map((row) => (
          <TableRow key={row.importCategoryId}>
            <TableCell>{row.item}</TableCell>
            <TableCell align="right">
              <Tooltip title="View import items in this category">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => handleViewCategoryItems(row.importCategoryId, row.item)}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Remove">
                <IconButton size="small" color="error" onClick={() => onRemove(row.importCategoryId)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  const inputItemRows = selectedInputRows.filter((r) => r.importId != null);
  const inputCatRows = selectedInputRows.filter((r) => r.importCategoryId != null);
  const outputItemRows = selectedOutputRows.filter((r) => r.importId != null);
  const outputCatRows = selectedOutputRows.filter((r) => r.importCategoryId != null);

  if (isLoading) {
    return <Typography sx={{ p: 3 }}>Loading product data...</Typography>;
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={11}>
        <Typography mt={1} variant="h6">Edit Sub Product</Typography>
      </Grid>
      <Grid item xs={12} sm={1}>
        <Tooltip title="Close">
          <IconButton onClick={() => setOpenForm(false)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Grid>

      <Grid item xs={12}>
        <Box
          sx={{
            display: "flex", alignItems: "center", gap: 1, p: 1.5, borderRadius: 1,
            bgcolor: (theme) => alpha(theme.palette.info.main, 0.08),
            border: (theme) => `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
          }}
        >
          <InfoOutlinedIcon color="info" fontSize="small" />
          <Typography variant="body2" color="info.main">
            Changes to this product will apply from the next sub order onwards. Existing orders will not be affected.
          </Typography>
        </Box>
      </Grid>

      <Grid item xs={12} sm={3}>
        <InputField
          name="_productName"
          value={values._productName}
          onChange={(event, newInputValue) => handleChange(event, newInputValue)}
          type="text"
          label="Sub Product Name"
        />
      </Grid>
      <Grid item xs={12} />

      {/* ── Input section ── */}
      <Grid item xs={12}>
        <Grid container spacing={1} alignItems="center">
          <Grid item>
            <Button
              onClick={() => setOpenInputTable(true)}
              variant="outlined"
              style={{ borderColor: error._inputItems ? "#f44336" : undefined }}
            >
              Select Input Items
            </Button>
          </Grid>
          <Grid item>
            <Button
              onClick={() => setOpenInputCategoryTable(true)}
              variant="outlined"
              style={{ borderColor: error._inputItems ? "#f44336" : undefined }}
            >
              Select Input Categories
            </Button>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        {inputItemRows.length > 0 && (
          <BillWritableTable
            columns={itemColumns}
            rows={inputItemRows}
            setRows={(updated) => setSelectedInputRows([...updated, ...inputCatRows])}
            page={writeable_page}
            setPage={() => {}}
            rowsPerPage={writeable_rowsPerPage}
            tablePagin={false}
            setRowsPerPage={() => {}}
          />
        )}
        {inputCatRows.length > 0 && renderCategoryTable(inputCatRows, removeInputCategoryRow)}
        {error._inputItems && (
          <Typography variant="caption" style={{ color: "red", fontSize: 14, fontWeight: "bold" }}>
            {error._inputItems}
          </Typography>
        )}
      </Grid>

      {/* ── Output section ── */}
      <Grid item xs={12}>
        <Grid container spacing={1} alignItems="center">
          <Grid item>
            <Button
              onClick={() => setOpenOutputTable(true)}
              variant="outlined"
              style={{ borderColor: error._outputItems ? "#f44336" : undefined }}
            >
              Select Output Items
            </Button>
          </Grid>
          <Grid item>
            <Button
              onClick={() => setOpenOutputCategoryTable(true)}
              variant="outlined"
              style={{ borderColor: error._outputItems ? "#f44336" : undefined }}
            >
              Select Output Categories
            </Button>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        {outputItemRows.length > 0 && (
          <BillWritableTable
            columns={itemColumns}
            rows={outputItemRows}
            setRows={(updated) => setSelectedOutputRows([...updated, ...outputCatRows])}
            page={writeable_page}
            setPage={() => {}}
            rowsPerPage={writeable_rowsPerPage}
            tablePagin={false}
            setRowsPerPage={() => {}}
          />
        )}
        {outputCatRows.length > 0 && renderCategoryTable(outputCatRows, removeOutputCategoryRow)}
        {error._outputItems && (
          <Typography variant="caption" style={{ color: "red", fontSize: 14, fontWeight: "bold" }}>
            {error._outputItems}
          </Typography>
        )}
      </Grid>

      {error._duplicateItems && (
        <Grid item xs={12}>
          <Typography variant="caption" style={{ color: "red", fontSize: 16, fontWeight: "bold", paddingLeft: 40 }}>
            {error._duplicateItems}
          </Typography>
        </Grid>
      )}

      <Grid item xs={12}>
        {displayAlert && <FormAlert type={alertData.type} message={alertData.message} />}
      </Grid>

      <Grid item xs={12}>
        <Grid container justifyContent="space-between">
          <Grid item xs={12} sm={4}>
            <Button fullWidth variant="contained" onClick={handleSubmitClick}>Save Changes</Button>
          </Grid>
          <Grid item xs={12} sm={1}>
            <Button onClick={() => setOpenForm(false)} variant="outlined">Cancel</Button>
          </Grid>
        </Grid>
      </Grid>

      {/* ── Confirm edit dialog ── */}
      <Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)}>
        <DialogTitle>Confirm Edit</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Changes to this product will apply from the <strong>next sub order</strong> onwards.
            Existing orders will not be affected. Do you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmDialog(false)}>Cancel</Button>
          <Button onClick={handleConfirmEdit} variant="contained" color="primary">Confirm</Button>
        </DialogActions>
      </Dialog>

      {/* ── Import item popups ── */}
      {openInputTable && (
        <ItemSelectingPopup
          setOpenTable={setOpenInputTable}
          openTable={openInputTable}
          handleCloseTable={handleCloseInputTable}
          setSelectedItems={setSelectedInputItems}
          selectedItems={selectedInputItems}
          setRows={setRows}
          rows={rows}
        />
      )}
      {openOutputTable && (
        <ItemSelectingPopup
          setOpenTable={setOpenOutputTable}
          openTable={setOpenOutputTable}
          handleCloseTable={handleCloseOutputTable}
          setSelectedItems={setSelectedOutputItems}
          selectedItems={selectedOutputItems}
          setRows={setRows}
          rows={rows}
          isOneChoise={true}
        />
      )}

      {/* ── Import category popups ── */}
      {openInputCategoryTable && (
        <Im_CategorySelectingPopup
          setOpenCatgoryTable={setOpenInputCategoryTable}
          openCatgoryTable={openInputCategoryTable}
          handleCloseCatgoryTable={handleCloseInputCategoryTable}
          setSelectedCatgory={setSelectedInputCategories}
          selectedCatgory={selectedInputCategories}
          catgoryRows={inputCategoryRows}
          setCatgoryRows={setInputCategoryRows}
        />
      )}
      {openOutputCategoryTable && (
        <Im_CategorySelectingPopup
          setOpenCatgoryTable={setOpenOutputCategoryTable}
          openCatgoryTable={openOutputCategoryTable}
          handleCloseCatgoryTable={handleCloseOutputCategoryTable}
          setSelectedCatgory={setSelectedOutputCategories}
          selectedCatgory={selectedOutputCategories}
          catgoryRows={outputCategoryRows}
          setCatgoryRows={setOutputCategoryRows}
        />
      )}

      {/* ── Category items view dialog ── */}
      <CategoryItemsViewDialog
        open={categoryViewOpen}
        onClose={() => setCategoryViewOpen(false)}
        categoryName={categoryViewName}
        items={categoryViewItems}
      />
    </Grid>
  );
}

export default SubOrderProductEdit;
